"use client"
import React, { useState, useEffect } from "react";
import "@/components/locator/LocatorSearchResultsComponent.css";
import ServiceProfileItemSummary from "@/components/locator/ServiceProfileItemSummary";
import DistanceView from "@/components/shared/DistanceView";
import * as turf from "@turf/turf";
import { Tab } from "react-bootstrap";
import LocatorSearchResultCard from "@/components/locator/LocatorSearchResultCard";
import { useMediaQuery } from "react-responsive";
import LocatorSearchResultPreview from "@/components/locator/LocatorSearchResultPreview";
import RESearchResultPreview from "@/components/locator/re/RESearchResultPreview";

const LocatorSearchResultsComponent = ({
  searchMode,
  searchResults,
  community,
  selectedItem,
  onItemHovered,
  onItemSelected,
  selectedSearchOption,
  textQuery,
  currentLocation,
  currentFilter,
  businessMultipleLocations,
  heading = "Results",
  // when given, a Clear button next to the heading calls it
  onClear,
  emptyText = "No results could be found for your search."
}) => {
  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });

  const [sortMode, setSortMode] = useState(
    searchMode === "business" ? "name" : "best"
  );
  const [sortedSearchResults, setSortedSearchResults] = useState([]);
  const [timestamp, setTimestamp] = useState(new Date().getTime());
  const [activeTab, setActiveTab] = useState("results");

  useEffect(() => {
    if (
      !businessMultipleLocations &&
      selectedItem &&
      (isSm || (!selectedSearchOption && !textQuery))
    ) {
      setActiveTab("selected-item");
    } else {
      setActiveTab("results");
    }
  }, [selectedItem, selectedSearchOption]);

  useEffect(() => {
    if (searchResults && sortMode) {
      setSortedSearchResults(sortSearchResults(searchResults));
    }
  }, [searchResults, sortMode]);

  const getLowestAcquireScore = business => {
    const listings = business?.serviceProfile?.serviceListings ?? [];
    if (listings.length === 0) return Infinity;
    return Math.min(...listings.map(l => l.bestUseScoreAcquire ?? Infinity));
  };

  const getLowestDiscardScore = business => {
    const listings = business?.serviceProfile?.serviceListings ?? [];
    if (listings.length === 0) return Infinity;
    return Math.min(...listings.map(l => l.bestUseScoreDiscard ?? Infinity));
  };

  const getLowestAverageScore = business => {
    const listings = business?.serviceProfile?.serviceListings ?? [];
    if (listings.length === 0) return Infinity;
    return Math.min(...listings.map(l => l.bestUseScore ?? Infinity));
  };

  const sortSearchResults = searchResults => {
    setTimestamp(new Date().getTime());
    // console.log("sortSearchResults", searchResults, sortMode, currentFilter);
    if (sortMode === "best") {
      let sortedResults;
      if (
        currentFilter?.selectedAction &&
        acquireActions.includes(currentFilter?.selectedAction)
      ) {
        sortedResults = searchResults.sort((a, b) => {
          const scoreA = getLowestAcquireScore(a);
          const scoreB = getLowestAcquireScore(b);
          const numAccoladesA = a.communityAccoladeIds?.length ?? 0;
          const numAccoladesB = b.communityAccoladeIds?.length ?? 0;
          const accoladeCompare = numAccoladesB - numAccoladesA;
          return accoladeCompare !== 0 ? accoladeCompare : scoreA - scoreB;
        });
      } else if (
        currentFilter?.selectedAction &&
        discardActions.includes(currentFilter?.selectedAction)
      ) {
        sortedResults = searchResults.sort((a, b) => {
          const scoreA = getLowestDiscardScore(a);
          const scoreB = getLowestDiscardScore(b);
          const numAccoladesA = a.communityAccoladeIds?.length ?? 0;
          const numAccoladesB = b.communityAccoladeIds?.length ?? 0;
          const accoladeCompare = numAccoladesB - numAccoladesA;
          return accoladeCompare !== 0 ? accoladeCompare : scoreA - scoreB;
        });
      } else {
        sortedResults = searchResults.sort((a, b) => {
          const scoreA = getLowestAverageScore(a);
          const scoreB = getLowestAverageScore(b);
          const numAccoladesA = a.communityAccoladeIds?.length ?? 0;
          const numAccoladesB = b.communityAccoladeIds?.length ?? 0;
          const accoladeCompare = numAccoladesB - numAccoladesA;
          return accoladeCompare !== 0 ? accoladeCompare : scoreA - scoreB;
        });
      }
      // console.log("sortedResults", sortedResults);
      return sortedResults;
    }
    if (sortMode === "distance") {
      return searchResults.sort((a, b) => {
        if (!a.location && !b.location) return 0;
        if (!a.location) return 1;
        if (!b.location) return -1;

        const distanceA = turf.distance(
          [currentLocation.longitude, currentLocation.latitude],
          [a.location.longitude, a.location.latitude]
        );
        const distanceB = turf.distance(
          [currentLocation.longitude, currentLocation.latitude],
          [b.location.longitude, b.location.latitude]
        );
        return distanceA - distanceB;
      });
    } else if (sortMode === "name") {
      return searchResults.sort((a, b) => {
        // RE listings have no displayName; they sort by address
        const nameOf = result => result.displayName ?? result.addressLine1 ?? "";
        return nameOf(a).localeCompare(nameOf(b));
      });
    } else if (sortMode === "iwant") {
      return searchResults.sort((a, b) => {
        const scoreA = getLowestAcquireScore(a);
        const scoreB = getLowestAcquireScore(b);
        return scoreA - scoreB;
      });
    } else if (sortMode === "getridof") {
      return searchResults.sort((a, b) => {
        const scoreA = getLowestDiscardScore(a);
        const scoreB = getLowestDiscardScore(b);
        return scoreA - scoreB;
      });
    }
  };

  const acquireActions = ["BUY", "RENT", "BORROW", "DONATE"];
  const discardActions = ["SELL", "RECYCLE", "COMPOST", "DISPOSE"];

  return (
    <div className="locator-search-results-component">
      <Tab.Container activeKey={activeTab}>
        <Tab.Content>
          <Tab.Pane eventKey="results">
            <div
              className="default-flex-row-layout"
              style={{ padding: "0.625rem 1.5rem" }}
            >
              <h4>{heading}</h4>
              {onClear && (
                <button
                  type="button"
                  className="a"
                  style={{ marginLeft: "0.75rem" }}
                  onClick={onClear}
                >
                  Clear
                </button>
              )}
              <div className="flex-grow" />
              <span>Sort by:</span>
              <select
                id="search-results-sort-select"
                key="search-results-sort-select"
                style={{ minWidth: "160px" }}
                className="a"
                onChange={e => {
                  setSortMode(e.target.value);
                }}
              >
                {searchMode === "service" && (
                  <option key="search-results-sort-best" value="best">
                    Best Use
                  </option>
                )}
                <option key="search-results-sort-name" value="name">
                  Name
                </option>
                {currentLocation && !currentLocation.error && (
                  <option key="search-results-sort-distance" value="distance">
                    Distance
                  </option>
                )}
              </select>
            </div>
            <div className="locator-search-results-list">
              {searchResults && searchResults.length === 0 && (
                <div
                  className="default-flex-row-layout"
                  style={{ padding: "0.625rem 1.5rem" }}
                >
                  <p>{emptyText}</p>
                </div>
              )}
              {sortedSearchResults?.map((result, index) => {
                return (
                  <div
                    key={`${result.id}-${timestamp}-${index}`}
                    id={`${result.id}-${timestamp}`}
                    className={`locator-search-result-item ${
                      selectedItem?.id === result.id ? "selected" : ""
                    }`}
                    onMouseEnter={() => onItemHovered(result)}
                    onMouseLeave={() => onItemHovered(null)}
                    onClick={() => onItemSelected(result)}
                  >
                    {searchMode === "re" ? (
                      <RESearchResultPreview
                        item={result}
                        upperRight={
                          <>
                            {currentLocation && !currentLocation.error ? (
                              <div className="location-tag tags">
                                <span className="location">
                                  <DistanceView
                                    from={[
                                      currentLocation.longitude,
                                      currentLocation.latitude
                                    ]}
                                    to={[result.longitude, result.latitude]}
                                    suffix="away"
                                  />
                                </span>
                              </div>
                            ) : (
                              <></>
                            )}
                          </>
                        }
                        showThumbnail={true}
                      />
                    ) : (
                      <LocatorSearchResultPreview
                        item={result}
                        selectedSearchOption={selectedSearchOption}
                        searchMode={searchMode}
                        showThumbnail={searchMode === "business"}
                        upperRight={
                          <>
                            {currentLocation && !currentLocation.error ? (
                              <div className="location-tag tags">
                                <span className="location">
                                  <DistanceView
                                    location={result.location}
                                    from={[
                                      currentLocation.longitude,
                                      currentLocation.latitude
                                    ]}
                                    to={[
                                      result.location.longitude,
                                      result.location.latitude
                                    ]}
                                    suffix="away"
                                  />
                                </span>
                              </div>
                            ) : (
                              <></>
                            )}
                          </>
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="selected-item">
            <div className="locator-search-results-item">
              <div
                className="upper-left"
                style={{
                  position: "absolute",
                  top: "1.5rem",
                  left: "1.5rem",
                  zIndex: 2
                }}
              >
                <div
                  className="close-button"
                  onClick={() => {
                    setActiveTab("results");
                    onItemSelected(null);
                  }}
                >
                  <i className="fa-solid fa-arrow-left"></i>
                </div>
              </div>
              <LocatorSearchResultCard
                community={community}
                item={selectedItem}
                searchMode={searchMode}
                selectedSearchOption={selectedSearchOption}
                onBack={() => setActiveTab("results")}
              />
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default LocatorSearchResultsComponent;
